import { useState } from "react";
import { Sheet } from "react-modal-sheet";
import { useTranslation } from "react-i18next";
import { Share2 } from "lucide-react";
import IconClose from "@/assets/icons/close.svg?react";
import VibratingButton from "@/components/base/button/VibratingButton";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import { formatNumber } from "@/shared/utils";
import ShareChekSheet from "@/pages/p2p/components/ShareChekSheet.tsx";
import ArrowUpRed from "@/assets/icons/arrow-narrow-up.svg?react";
import ArrowDown from "@/assets/icons/daily_checkin.svg?react";
import dayjs from "dayjs";
import { cn } from "@/lib/utils.ts";
import { HistoryPurposes, Transaction } from "./types.ts";

interface HistoryModalSheetProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
}

const HistoryModalSheet = ({
  isOpen,
  onClose,
  transaction,
}: HistoryModalSheetProps) => {
  const { t } = useTranslation();
  // The chek share UX is the same in-app Drawer the post-send P2P receipt
  // uses (ShareChekSheet) — opened with the transfer_id so the backend
  // renders / stores / delivers the PDF. Keeps both share entry points
  // identical instead of forking a separate client-render path here.
  // This react-modal-sheet sits at z-index 9999 while open, so the Drawer
  // carries its own raised z-index to render on top (see ShareChekSheet).
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);

  const isP2P =
    transaction?.purpose === HistoryPurposes.P2P_TRANSFER && transaction.p2p;

  return (
    <>
    <Sheet
      isOpen={isOpen}
      detent="content-height"
      onClose={onClose}
      tweenConfig={{ ease: [0.16, 1, 0.3, 1], duration: 0.55 }}
    >
      <Sheet.Container className="!bg-gray-4 rounded-t-2xl">
        <Sheet.Header className="[&_.react-modal-sheet-header]:!py-2 [&_.react-modal-sheet-header]:!h-5 !bg-gray-4 rounded-t-2xl" />
        <Sheet.Content className="bg-gray-4 px-4 pb-safe-bottom">
          <div className="flex justify-between">
            <h2></h2>
            <VibratingButton type="button" onClick={onClose}>
              <IconClose className="text-gray-1 size-6 cursor-pointer" />
            </VibratingButton>
          </div>
          {transaction ? (
            <div className="">
              <div className="bg-gray-4">
              <div className="flex flex-col items-center justify-center mb-5">
                <div className="bg-white/08 p-2 rounded-2xl flex items-center justify-center shrink-0">
                  {transaction.type === 1 ? (
                    <ArrowDown className="size-12" />
                  ) : (
                    <ArrowUpRed className="size-12" />
                  )}
                </div>
                <p className="text-sm leading-4 font-semibold text-white mt-2 text-center">
                  {transaction.display_title}
                </p>
              </div>
              <div className="space-y-2 bg-white/08 mb-6 border-b px-3 rounded-xl border-white/08">
                {transaction.purpose === HistoryPurposes.P2P_TRANSFER &&
                transaction.p2p ? (
                  // P2P transfers carry counterparty + commission + the
                  // underlying transfer_id, which neither the income nor
                  // outcome default views surface. Render a dedicated
                  // block instead of falling through to the generic ones.
                  <>
                    <p className="text-sm py-3 border-b border-white/08 flex items-center justify-between text-white">
                      <p className="text-xs leading-130 font-normal shrink-0">
                        {transaction.type === 1
                          ? t("p2p_receipt_sender") || "Yuboruvchi"
                          : t("p2p_receipt_receiver") || "Qabul qiluvchi"}
                        :
                      </p>
                      <p className="text-xs font-medium leading-130 text-right">
                        {transaction.p2p.counterparty_username
                          ? `@${transaction.p2p.counterparty_username}`
                          : transaction.p2p.counterparty_full_name || "—"}
                      </p>
                    </p>
                    <p className="text-sm pb-3 border-b border-white/08 flex items-center justify-between text-white">
                      <p className="text-xs leading-130 font-normal">
                        {t("operation_date")}:
                      </p>
                      <p className="text-xs font-medium leading-130">
                        {dayjs(transaction.created_at).format(
                          "DD.MM.YYYY, HH:mm"
                        )}
                      </p>
                    </p>
                    {transaction.p2p.transfer_id && (
                      <p className="text-sm pb-3 border-b border-white/08 flex items-center justify-between text-white">
                        <p className="text-xs leading-130 font-normal shrink-0">
                          {t("transactionId")}:
                        </p>
                        <p className="text-xs font-medium leading-130 text-right break-all">
                          {transaction.p2p.transfer_id}
                        </p>
                      </p>
                    )}
                    {transaction.type === -1 &&
                      transaction.p2p.commission_uzs && (
                        <p className="text-sm pb-3 border-b border-white/08 flex items-center justify-between text-white">
                          <p className="text-xs leading-130 font-normal">
                            {t("p2p_receipt_commission") || "Komissiya"}:
                          </p>
                          <p className="text-xs font-medium leading-130">
                            {formatNumber(transaction.p2p.commission_uzs)} UZS
                          </p>
                        </p>
                      )}
                    <p className="text-sm pb-3 flex items-center justify-between text-white">
                      <p className="font-normal text-xs leading-130">
                        {transaction.type === 1
                          ? t("received_don")
                          : t("taken_don")}
                        :
                      </p>
                      <span
                        className={cn(
                          "flex-y-center gap-0.5 text-xs font-medium leading-130",
                          transaction.type === -1 ? "text-red" : "text-green"
                        )}
                      >
                        {transaction.type === 1
                          ? `+${formatNumber(transaction.amount)}`
                          : `-${formatNumber(transaction.amount)}`}
                        {" DON"}
                      </span>
                    </p>
                  </>
                ) : transaction.type === 1 ? (
                  <>
                    <p className="text-sm py-3 border-b border-white/08 flex items-center justify-between text-white">
                      <p className="text-xs leading-130 font-normal shrink-0">
                        {t("transactionId")}:
                      </p>
                      <p className="text-xs font-medium leading-130 text-right">
                        {transaction.id}
                      </p>
                    </p>
                    <p className="text-sm pb-3 border-b border-white/08 flex items-center justify-between text-white">
                      <p className="text-xs leading-130 font-normal">
                        {t("time")}:
                      </p>{" "}
                      <p className="text-xs font-medium leading-130">
                        {dayjs(transaction.created_at).format(
                          "DD.MM.YYYY, HH:mm"
                        )}
                      </p>
                    </p>
                    <p className="text-sm pb-3 border-b border-white/08 flex items-center justify-between text-white">
                      <p className="text-xs leading-130 font-normal">
                        {t("description")}:
                      </p>
                      <p className="text-xs font-medium leading-130">
                        {transaction.purpose === HistoryPurposes.EXTERNAL_APP
                          ? transaction.application?.name
                          : t(
                              `award_text.${transaction.purpose}`,
                              transaction.purpose
                            )}{" "}
                        {transaction.purpose === HistoryPurposes.EXTERNAL_APP}
                      </p>
                    </p>
                    <p className="text-sm pb-3   flex items-center justify-between text-white">
                      <p className="font-normal text-xs leading-130">
                        {t("received_don")}:
                      </p>{" "}
                      <span
                        className={cn(
                          "flex-y-center text-xs font-medium leading-130 gap-0.5",
                          "text-green"
                        )}
                      >
                        {`+${formatNumber(transaction.amount)}`}
                        {" DON"}
                      </span>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm py-3 border-b border-white/08 flex items-center justify-between text-white">
                      <p className="text-xs font-normal leading-130">
                        {t("operation_date")}:
                      </p>{" "}
                      <p className="text-xs leading-130 font-medium">
                        {" "}
                        {dayjs(transaction.created_at).format(
                          "DD.MM.YYYY, HH:mm"
                        )}
                      </p>
                    </p>
                    <p className="text-sm pb-3 border-b border-white/08 flex items-center justify-between text-white">
                      <p className="text-xs font-normal leading-130">
                        {t("description")}:
                      </p>
                      <p className="text-xs leading-130 font-medium">
                        {transaction.purpose === HistoryPurposes.EXTERNAL_APP
                          ? transaction.application?.name || t("unknown_app")
                          : t(
                              `award_text.${transaction.purpose}`,
                              transaction.purpose
                            )}
                      </p>
                    </p>
                    <p className="text-sm pb-3 flex items-center justify-between text-white">
                      <p className="text-xs font-normal leading-130">
                        {t("taken_don")}:
                      </p>{" "}
                      <span
                        className={cn(
                          "flex-y-center gap-0.5 leading-130 font-medium text-xs",
                          transaction.type === -1 ? "text-red" : "text-green"
                        )}
                      >
                        {`-${formatNumber(transaction.amount)}`}
                        {" DON"}
                      </span>
                    </p>
                  </>
                )}
                {transaction.application?.logo &&
                  transaction.purpose === HistoryPurposes.EXTERNAL_APP && (
                    <p className="text-sm py-3 border-b border-white/08 flex items-center justify-between text-white">
                      <strong>{t("app_logo")}:</strong>{" "}
                      <img
                        src={transaction.application.logo}
                        alt={transaction.application.name}
                        className="inline w-6 h-6 rounded"
                      />
                    </p>
                  )}
              </div>
              </div>
              {/* Share — only for P2P transfers where the rendered card
                  is a self-contained "chek" worth sharing. Other purposes
                  (daily checkin, task awards) are reward records, not
                  receipts, and the share UX makes less sense. */}
              {isP2P && (
                <BaseButton
                  className="w-full mb-4"
                  variant="secondary"
                  text={t("p2p_receipt_share") || "Chekni ulashish"}
                  onClick={() => setIsShareSheetOpen(true)}
                  vibrate
                  vibrationType="soft"
                  prefix={<Share2 className="size-4" />}
                />
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-1">
              {t("no_transaction_selected")}
            </p>
          )}
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop className="!bg-[#0E0E14]/80" onTap={onClose} />
    </Sheet>
    <ShareChekSheet
      isOpen={isShareSheetOpen}
      onClose={() => setIsShareSheetOpen(false)}
      transferId={transaction?.p2p?.transfer_id ?? null}
    />
    </>
  );
};

export default HistoryModalSheet;
